import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, apiService, usersService, settingsService } from '../services/api';
import { sortInvoices } from '../utils/sortInvoices';

const InvoiceContext = createContext();

export const useInvoice = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoice must be used within InvoiceProvider');
  }
  return context;
};

export const InvoiceProvider = ({ children }) => {
  const [_currentUser, _setCurrentUser] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const currentUser = impersonating || _currentUser;
  const [allInvoices, setAllInvoices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [branding, setBranding] = useState({ logoUrl: null, primaryColor: null, secondaryColor: null });
  const [permissions, setPermissions] = useState(null);
  const [userPermOverrides, setUserPermOverrides] = useState({});
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    return {
      vendor: '',
      startDate: '',
      endDate: '',
      search: '',
      year: String(now.getFullYear()),
      month: String(now.getMonth() + 1).padStart(2, '0'),
    };
  });
  const [sortConfig, setSortConfig] = useState({
    column: 'createdAt',
    direction: 'DESC'
  });
  const [quizRunning, setQuizRunning] = useState(false);

  // Initialize - load branding and check if user is logged in
  useEffect(() => {
    loadBranding();
    // Clear any stale impersonation so loadCurrentUser always fetches the real user
    sessionStorage.removeItem('impersonatingId');
    const token = localStorage.getItem('token');
    if (token) {
      loadCurrentUser();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load permissions
  const loadPermissions = async (userId) => {
    const uid = userId || currentUser?.id;
    try {
      const perms = await settingsService.getPermissions();
      setPermissions(perms);
      if (uid) {
        const overrides = await settingsService.getUserPermissions(uid);
        setUserPermOverrides(overrides || {});
      }
    } catch (error) {
      // fail silently — defaults will apply
    }
  };

  // Refresh permissions in background so overrides take effect without re-login
  useEffect(() => {
    if (!currentUser?.id) return;
    const uid = currentUser.id;
    const refresh = () => loadPermissions(uid);
    const interval = setInterval(refresh, 10_000);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load current user
  const loadCurrentUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      _setCurrentUser(user);
      await Promise.all([loadInvoices(), loadPermissions(user.id)]);
    } catch (error) {
      console.error('Load user error:', error);
      localStorage.removeItem('token');
      setLoading(false);
    }
  };

  // Load invoices (once, without filters)
  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await apiService.getInvoices({});
      setAllInvoices(data.invoices);
    } catch (error) {
      showNotification(error.message || 'Failed to load invoices', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Branding
  const applyBrandingToDom = ({ primaryColor, secondaryColor }) => {
    if (primaryColor) {
      document.documentElement.style.setProperty('--color-primary', primaryColor);
    }
    if (secondaryColor) {
      document.documentElement.style.setProperty('--color-secondary', secondaryColor);
    }
  };

  const loadBranding = async () => {
    try {
      const data = await settingsService.getBranding();
      const b = { logoUrl: data.logoUrl, primaryColor: data.primaryColor, secondaryColor: data.secondaryColor };
      setBranding(b);
      applyBrandingToDom(b);
    } catch (error) {
      // Branding is optional — fail silently
    }
  };

  const updateBranding = async ({ primaryColor, secondaryColor }) => {
    try {
      const updates = [];
      if (primaryColor !== undefined) updates.push(settingsService.set('brand_primary_color', primaryColor));
      if (secondaryColor !== undefined) updates.push(settingsService.set('brand_secondary_color', secondaryColor));
      await Promise.all(updates);
      const next = { ...branding };
      if (primaryColor !== undefined) next.primaryColor = primaryColor;
      if (secondaryColor !== undefined) next.secondaryColor = secondaryColor;
      setBranding(next);
      applyBrandingToDom(next);
      showNotification('Brand colors updated', 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to update branding', 'error');
    }
  };

  // Filter invoices locally
  useEffect(() => {
    if (!currentUser) return;

    let filtered = [...allInvoices];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(inv =>
        (inv.vendorName?.toLowerCase().includes(searchLower)) ||
        (inv.notes?.toLowerCase().includes(searchLower)) ||
        (inv.amount != null && String(parseFloat(inv.amount).toFixed(2)).includes(searchLower)) ||
        (inv.uploader?.firstName?.toLowerCase().includes(searchLower)) ||
        (inv.invoiceDate?.includes(searchLower))
      );
    }

    // Vendor filter
    if (filters.vendor) {
      const vendorLower = filters.vendor.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.vendorName?.toLowerCase().includes(vendorLower)
      );
    }

    // Year filter
    if (filters.year) {
      filtered = filtered.filter(inv =>
        inv.invoiceDate && inv.invoiceDate.startsWith(filters.year)
      );
    }

    // Month filter (combined with year)
    if (filters.month && filters.year) {
      const ym = `${filters.year}-${filters.month}`;
      filtered = filtered.filter(inv =>
        inv.invoiceDate && inv.invoiceDate.startsWith(ym)
      );
    }

    // Date filter (single date or range)
    if (filters.startDate && filters.endDate) {
      // Date range: filter between start and end
      filtered = filtered.filter(inv =>
        inv.invoiceDate >= filters.startDate && inv.invoiceDate <= filters.endDate
      );
    } else if (filters.startDate && !filters.endDate) {
      // Only start date: filter to exact date
      filtered = filtered.filter(inv => inv.invoiceDate === filters.startDate);
    } else if (filters.endDate && !filters.startDate) {
      // Only end date: filter to exact date
      filtered = filtered.filter(inv => inv.invoiceDate === filters.endDate);
    }

    // Sort
    const sorted = sortInvoices(filtered, sortConfig);

    setInvoices(sorted);
  }, [allInvoices, filters, currentUser, sortConfig]);

  // Auth functions
  const login = async (loginId, password) => {
    try {
      const data = await authService.login(loginId, password);
      localStorage.setItem('token', data.token);
      _setCurrentUser(data.user);
      showNotification('Login successful', 'success');
      await Promise.all([loadInvoices(), loadPermissions(data.user.id)]);
      return true;
    } catch (error) {
      showNotification(error.message || 'Login failed', 'error');
      return false;
    }
  };

  const register = async (firstName, lastName, loginId, password) => {
    try {
      const data = await authService.register(firstName, lastName, loginId, password);
      localStorage.setItem('token', data.token);
      _setCurrentUser(data.user);
      showNotification('Registration successful', 'success');
      await loadInvoices();
      return true;
    } catch (error) {
      showNotification(error.message || 'Registration failed', 'error');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('impersonatingId');
    _setCurrentUser(null);
    setImpersonating(null);
    setInvoices([]);
    showNotification('Logged out successfully', 'success');
  };

  // Invoice operations
  const uploadInvoice = async (formData) => {
    try {
      const invoice = await apiService.uploadInvoice(formData);
      setAllInvoices([invoice, ...allInvoices]);
      showNotification('Invoice uploaded successfully', 'success');
      if (formData.get('paymentMethod') === 'credit_balance') {
        const deducted = parseFloat(formData.get('amount'));
        _setCurrentUser(prev => ({
          ...prev,
          creditBalance: (parseFloat(prev.creditBalance || 0) - deducted).toFixed(2)
        }));
      }
      return true;
    } catch (error) {
      showNotification(error.message || 'Upload failed', 'error');
      return false;
    }
  };

  const updateInvoice = async (id, data) => {
    try {
      const updated = await apiService.updateInvoice(id, data);
      setAllInvoices(allInvoices.map(inv => inv.id === id ? updated : inv));
      showNotification('Invoice updated successfully', 'success');
      return true;
    } catch (error) {
      showNotification(error.message || 'Update failed', 'error');
      return false;
    }
  };

  const deleteInvoice = async (id) => {
    try {
      await apiService.deleteInvoice(id);
      setAllInvoices(allInvoices.filter(inv => inv.id !== id));
      showNotification('Invoice deleted successfully', 'success');
      return true;
    } catch (error) {
      showNotification(error.message || 'Delete failed', 'error');
      return false;
    }
  };

  const downloadInvoice = async (id, filename) => {
    try {
      await apiService.downloadInvoice(id, filename);
      showNotification('Download started', 'success');
    } catch (error) {
      showNotification(error.message || 'Download failed', 'error');
    }
  };

  // Profile operations
  const updateProfile = async (id, data) => {
    try {
      const updatedUser = await usersService.updateProfile(id, data);
      _setCurrentUser(updatedUser);
      showNotification('Profile updated successfully', 'success');
      return true;
    } catch (error) {
      showNotification(error.message || 'Profile update failed', 'error');
      return false;
    }
  };

  const uploadPhoto = async (id, file) => {
    try {
      const updatedUser = await usersService.uploadPhoto(id, file);
      _setCurrentUser(updatedUser);
      showNotification('Photo updated successfully', 'success');
      return true;
    } catch (error) {
      showNotification(error.message || 'Photo upload failed', 'error');
      return false;
    }
  };

  const removePhoto = async (id) => {
    try {
      const updatedUser = await usersService.removePhoto(id);
      _setCurrentUser(updatedUser);
      showNotification('Photo removed', 'success');
      return true;
    } catch (error) {
      showNotification(error.message || 'Failed to remove photo', 'error');
      return false;
    }
  };

  const updatePassword = async (id, passwordData) => {
    try {
      await usersService.updatePassword(id, passwordData);
      showNotification('Password updated successfully', 'success');
      return true;
    } catch (error) {
      showNotification(error.message || 'Password update failed', 'error');
      return false;
    }
  };

  // Notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Permission checks
  const hasPermission = (key) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if (key in userPermOverrides) return userPermOverrides[key];
    if (!permissions) {
      // Before permissions load: fall back to role defaults
      return currentUser.role === 'admin';
    }
    return permissions[currentUser.role]?.[key] === true;
  };

  const canEdit = (invoice) => {
    if (!currentUser) return false;
    if (hasPermission('edit_delete_any_invoice')) return true;
    return currentUser.id === invoice.uploadedBy;
  };

  const canDelete = (invoice) => {
    if (!currentUser) return false;
    if (hasPermission('edit_delete_any_invoice')) return true;
    return currentUser.id === invoice.uploadedBy;
  };

  const isAdmin = () => {
    return ['admin', 'super_admin'].includes(currentUser?.role);
  };

  const isSuperAdmin = () => {
    return currentUser?.role === 'super_admin';
  };

  const startImpersonation = (user) => {
    sessionStorage.setItem('impersonatingId', user.id);
    setImpersonating(user);
    loadInvoices();
    loadPermissions(user.id);
  };
  const stopImpersonation = () => {
    sessionStorage.removeItem('impersonatingId');
    setImpersonating(null);
    loadInvoices();
    loadPermissions(_currentUser?.id);
  };

  const value = {
    currentUser,
    realUser: _currentUser,
    impersonating,
    startImpersonation,
    stopImpersonation,
    invoices,
    allInvoices,
    loading,
    notification,
    filters,
    setFilters,
    sortConfig,
    setSortConfig,
    login,
    register,
    logout,
    loadInvoices,
    uploadInvoice,
    updateInvoice,
    deleteInvoice,
    downloadInvoice,
    updateProfile,
    uploadPhoto,
    removePhoto,
    updatePassword,
    showNotification,
    canEdit,
    canDelete,
    isAdmin,
    isSuperAdmin,
    hasPermission,
    permissions,
    loadPermissions,
    branding,
    loadBranding,
    updateBranding,
    quizRunning,
    setQuizRunning
  };

  return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
};
